import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  geminiRequest,
  generateFirebaseId,
  getMediaObjects,
} from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import { v4 as uuidv4 } from "uuid";
import { cn } from "../../../../util/classNameConstructor";
import Button from "../../../core/button/Button";
import Icon from "../../../core/icon/Icon";
import { Input } from "../../../core/inputs/Input";
import ImageCard from "../../../molecules/cards/ImageCard";
import type { ImageStyle } from "../../createSurvey/imageStylesConfig";
import {
  AI_IMAGE_NO_TEXT_SUFFIX,
  getImageStylesConfig,
  IMAGE_STYLE_PROMPTS,
} from "../../createSurvey/imageStylesConfig";
import { deleteStorageFile } from "../../imageModifier/ActionBar";
import type { ImageInput } from "../../imageModifier/ImagesUploadModal";

type AiImageProps = {
  basePath: string;
  saveToPath?: string;
  selectedImages: ImageInput[];
  setSelectedImages: React.Dispatch<React.SetStateAction<ImageInput[]>>;
  generatedImage: {
    id: string;
    url: string;
    name: string;
    path: string;
    size: number;
    width?: number;
    height?: number;
  };
  setGeneratedImage: React.Dispatch<
    React.SetStateAction<{
      id: string;
      url: string;
      name: string;
      path: string;
      size: number;
      width?: number;
      height?: number;
    }>
  >;
  project?: {
    settings?: {
      originalLanguage?: string;
    };
  };
};

function safeSessionStorageGet(key: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionStorageSet(key: string, value: string): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore (e.g. storage disabled)
  }
}
const AiImage = ({
  basePath,
  saveToPath,
  setSelectedImages,
  generatedImage,
  setGeneratedImage,
  project,
}: AiImageProps) => {
  const { t } = useTranslation();
  const promptSessionKey = `senf:ai-image:prompt:${basePath}:${saveToPath ?? ""}`;
  const styleSessionKey = `senf:ai-image:style:${basePath}:${saveToPath ?? ""}`;
  const [prompt, setPrompt] = useState<string>(() => {
    return safeSessionStorageGet(promptSessionKey) ?? "";
  });
  const [style, setStyle] = useState<ImageStyle>(() => {
    const saved = safeSessionStorageGet(styleSessionKey);
    return (saved as ImageStyle) || "illustration";
  });
  const [loading, setLoading] = useState(false);
  const imageStylesConfig = getImageStylesConfig(t);

  useEffect(() => {
    // Persist while the user types so closing/reopening restores it.
    safeSessionStorageSet(promptSessionKey, prompt);
  }, [prompt, promptSessionKey]);

  useEffect(() => {
    safeSessionStorageSet(styleSessionKey, style);
  }, [style, styleSessionKey]);

  // const getImgFetch = async (prompt) => {
  //   const options = {
  //     method: "POST",
  //     headers: {
  //       Authorization: `Bearer ${import.meta.env.VITE_GETIMG_ACCESS_KEY}`,
  //       accept: "application/json",
  //       "content-type": "application/json",
  //     },
  //     body: JSON.stringify({
  //       // model: "lcm-dream-shaper-v8",
  //       prompt,
  //       width: 512,
  //       height: 512,
  //       steps: 4,
  //       seed: 0,
  //       output_format: "jpeg",
  //     }),
  //   };

  //   return fetch("https://api.getimg.ai/v1/flux-schnell/text-to-image", options)
  //     .then((response) => response.json())
  //     .then((response) => response.image) // Assuming the base64 image is here
  //     .catch((err) => console.error(err));
  // };

  const handleGenerate = async () => {
    setLoading(true);
    const stylePrompt = IMAGE_STYLE_PROMPTS[style];
    const finalPrompt = stylePrompt ? `${prompt}, ${stylePrompt}` : prompt;
    const body = `${finalPrompt}${AI_IMAGE_NO_TEXT_SUFFIX}, 1080px by 1080px, square format`;

    try {
      // const base64Image = await geminiRequest({
      //   body: `${finalPrompt}, 1080px by 1080px, square format`,
      //   assistantId: "manipulateImageGeneral",
      //   lang: "en",
      //   images: []
      // });

      const base64Image = await geminiRequest({
        body,
        assistantId: "createImage",
        lang: "en",
      });

      // const base64Image = await getImgFetch(finalPrompt);
      const blob = await (
        await fetch(`data:image/jpeg;base64,${base64Image}`)
      ).blob();

      // Sanitize the filename
      const sanitizedFilename =
        (prompt || uuidv4())
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, "-") // Replace any non-alphanumeric characters with hyphens
          .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
          .replace(/^-|-$/g, "") // Remove leading and trailing hyphens
          .slice(0, 50) + ".webp"; // Limit length and add extension

      const imageFile = new File([blob], sanitizedFilename, {
        type: "image/webp",
      });

      const [file] = await getMediaObjects({
        fileList: [imageFile],
        setPath: () => `${basePath}/${saveToPath}`,
        basePath,
        options: {
          addToCache: false,
          metadata: {
            customMetadata: {
              hidden: "true",
              lang:
                project?.settings?.originalLanguage ||
                i18n.resolvedLanguage ||
                "en",
            },
          },
        },
      });

      // Set the image as a data URL with the correct MIME type
      const fileWithId = { ...file, id: generateFirebaseId() };
      setGeneratedImage((current) => {
        if (current?.id) deleteStorageFile(current.id);
        return fileWithId;
      });
      // console.log(file.url);
      setSelectedImages([
        { url: fileWithId.url, alt: "", name: "", size: 0, type: "image" },
      ]);
    } catch (error) {
      console.error("Error generating image:", error);
      alert(
        "Error generating image. There might be an issue with harmful content.",
      );
      setLoading(false);
      // Optionally, you can add error handling here
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5 h-full">
      <div className="grid grid-cols-[auto_1fr] gap-5 h-full">
        <div className="flex w-[350px] flex-col justify-between overflow-y-auto">
          <div className="flex h-auto  max-h-max flex-col items-start justify-start gap-4">
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value || "")}
              placeholder={t("global:builder.imageGeneration.prompt")}
              rows={3}
              maxRows={5}
              type="textarea"
              classNames={{
                inputContainer: "bg-surface-default",
              }}
            />
            <div className="grid grid-cols-3 gap-3 w-full">
              {imageStylesConfig
                .filter(
                  (styleOption) => styleOption.value !== ("none" as ImageStyle),
                )
                .map((styleOption) => (
                  <ImageCard
                    key={styleOption.value}
                    data={{
                      icon: styleOption.icon,
                      img: styleOption.img,
                      title: styleOption.label,
                      value: styleOption.value,
                    }}
                    selected={style === styleOption.value}
                    onClick={() => setStyle(styleOption.value)}
                    classNames={{
                      text: "text-xs p-1 pl-2",
                    }}
                  />
                ))}
            </div>

            <Button
              text={t("util:generate", { val: t("global:image") })}
              classNameWrapper="w-full"
              onClick={handleGenerate}
              leadingIcon="ai-image"
              className="w-full"
              size="lg"
              variant={generatedImage?.url ? "outline" : "primary"}
              loading={loading}
              disabled={loading}
              disabledNotice={t("util:isBeingGenerated", {
                val: t("global:image"),
              })}
            />
          </div>
          <p
            className={cn(
              "body-sm text-text-subdued pt-5 duration-500 ease-expressive-out",
              generatedImage?.url && "pb-20",
            )}
          >
            {t("global:builder.aiImageNotice")}
          </p>
        </div>

        <div
          className={cn(
            "flex h-full w-full items-center justify-center overflow-hidden rounded-xl duration-500 ease-expressive-out",
            generatedImage?.url && "pb-20",
          )}
        >
          {generatedImage?.url ? (
            <img
              src={generatedImage?.url}
              alt="Generated AI"
              className="max-w-full max-h-full h-auto w-auto rounded-xl self-start" // Ensure rounded corners and correct sizing
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed text-5xl">
              <Icon
                name="ai-image"
                className="text-text-placeholder w-[1em] h-[1em]"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiImage;
